import { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import { trackAiSearchChipRemoval } from '@pkgs/components/utils/tracking';
import { type FilterChip, removeAiSearchFilterChip } from '@pkgs/components/utils/aiSearchChipUtils';
import { useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useFilterChips } from '@pkgs/components/hooks/useFilterChips';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';
import { useListings } from '@pkgs/components/hooks/queries/useListings';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import ArrowRightIcon from '@pkgs/components/assets/icons/small/Right_Arrow_Outlined.svg';
import ArrowLeftIcon from '@pkgs/components/assets/icons/small/Left_Arrow_Outlined.svg';
import { AiStarGradient } from '@pkgs/components/assets/icons/small/AiStarGradient';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { Chip } from '../Chip';

import styles from './InfoRow.module.css';

const SCROLL_AMOUNT = 160;
const SCROLL_THRESHOLD = 4;

export const AiExtractedFilterChipsRow = ({ isMapView }: { isMapView: boolean }) => {
  const { countryCode } = useAppContext();
  const { breakpoint } = useBreakpoint();
  const { isPending: isAiSearchFetching } = useAiSearch();
  const { isFetching: isListingsFetching } = useListings();
  const { placeDetailsList } = usePlaceDetails();
  const applyState = useSearchStore((state) => state.applyState);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const searchMode = useSearchStore((state) => state.searchMode);
  const promptValue = usePromptStore((store) => store.promptValue);
  const { pageContext, shopContext } = useSearchModuleTracking();
  const chipsRowRef = useRef<HTMLDivElement>(null);
  const chipsWrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { chips } = useFilterChips();

  const updateScrollState = useCallback(() => {
    const element = chipsWrapperRef.current;
    if (!element) return;
    setCanScrollLeft(element.scrollLeft > SCROLL_THRESHOLD);
    setCanScrollRight(element.scrollWidth - element.scrollLeft - element.clientWidth > SCROLL_THRESHOLD);
  }, []);

  const hasPerformedAiSearch = promptValue.trim().length > 0;
  const isResultsLoading = isAiSearchFetching || isListingsFetching;
  const isVisible = searchMode === SearchMode.AI && hasPerformedAiSearch && !isResultsLoading && chips.length > 0;

  useEffect(() => {
    if (!isVisible) return;

    updateScrollState();

    if (typeof window === 'undefined') return;

    const animationFrameId = window.requestAnimationFrame(updateScrollState);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [chips, breakpoint, isMapView, isVisible, updateScrollState]);

  useEffect(() => {
    const rowElement = chipsRowRef.current;
    const listElement = chipsWrapperRef.current;

    if (!isVisible || !rowElement || !listElement || typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    resizeObserver.observe(rowElement);

    if (listElement !== rowElement) {
      resizeObserver.observe(listElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isVisible, updateScrollState]);

  const handleScrollButtonClick = (direction: 'left' | 'right') => {
    chipsWrapperRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  const handleChipDelete = (chip: FilterChip) => {
    trackAiSearchChipRemoval({
      source: 'bar',
      chipVariant: chip.variant,
      pageContext,
      shopContext,
    });

    const nextState = removeAiSearchFilterChip({
      state: getStateSnapshot(),
      chip,
      marketCountryCode: countryCode,
      placeDetails: placeDetailsList,
    });

    applyState({ state: nextState });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.aiChipsRow} data-testid="search-components_info-row_ai-chips-row" ref={chipsRowRef}>
      <div
        className={styles.aiChipsList}
        data-gradient-right={canScrollRight}
        data-gradient-left={canScrollLeft}
        data-testid="search-components_info-row_ai-chips-list"
        ref={chipsWrapperRef}
        onScroll={updateScrollState}
      >
        {chips.map((chip) => (
          <Chip
            className={styles.aiChip}
            deleteButtonLabel={`Remove ${chip.label}`}
            icon={chip.variant === 'preference' ? <AiStarGradient /> : undefined}
            key={chip.id}
            label={chip.label}
            onDelete={chip.isRemovable ? () => handleChipDelete(chip) : undefined}
            variant={chip.variant === 'preference' ? 'ai' : 'default'}
          />
        ))}
      </div>
      <IconButton
        className={cn(styles.aiChipsScrollButton, styles.aiChipsScrollButtonLeft)}
        data-hidden={!canScrollLeft}
        icon={<ArrowLeftIcon />}
        label="Scroll filters left"
        onClick={() => handleScrollButtonClick('left')}
        shape="circle"
        size="xsmall"
        variant="tertiary"
      />
      <IconButton
        className={cn(styles.aiChipsScrollButton, styles.aiChipsScrollButtonRight)}
        icon={<ArrowRightIcon />}
        data-hidden={!canScrollRight}
        label="Scroll filters right"
        onClick={() => handleScrollButtonClick('right')}
        shape="circle"
        size="xsmall"
        variant="tertiary"
      />
    </div>
  );
};
